import { useZero } from "@rocicorp/zero/react";
import { appMutators } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";
import type { EquipmentImage } from "@/hooks/useEquipment";

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export const useUploadEquipmentImage = () => {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ equipmentId: string; file: File }, EquipmentImage>({
    success: "Image uploaded",
    failure: "Failed to upload image",
    run: async ({ equipmentId, file }) => {
      const res = await fetch("/api/storage/upload", {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          "X-File-Name": file.name,
        },
        body: file,
      });
      if (!res.ok) throw new Error("Failed to upload file");
      const { publicUrl } = await res.json();

      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.equipmentImages.create({
          id,
          equipmentID: equipmentId,
          imageURL: publicUrl,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment image create rejected",
      );

      return {
        id,
        equipment_id: equipmentId,
        image_url: publicUrl,
        display_order: null,
        created_at: new Date().toISOString(),
      };
    },
  });
};

export const useDeleteEquipmentImage = () => {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { imageId: string; imageUrl: string; equipmentId: string },
    void
  >({
    success: "Image deleted",
    failure: "Failed to delete image",
    run: async ({ imageId, imageUrl }) => {
      const urlParts = imageUrl.split("/");
      const key = urlParts[urlParts.length - 1];
      if (key) {
        await fetch(`/api/storage/${encodeURIComponent(key)}`, {
          method: "DELETE",
        });
      }

      const mutation = zero.mutate(
        appMutators.equipmentImages.delete({ id: imageId }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment image delete rejected",
      );
    },
  });
};
